import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../../../src/environments/environment';
import { GoogleAnalyticsService } from '../../../google-analytics.service';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { marked } from 'marked';

import { LOCALE_ID } from '@angular/core';
import { BlogDetailsPageService } from './blog-details-page.service';
import { ShareSocialMediaModalComponent } from '../../common/share-social-media-modal/share-social-media-modal.component';
import { RelatedPostsDataService } from '../../../related-posts-data.service';
import { RecommendedArticlesService } from '../../../components/common/recommended-articles/recommended-articles.service';
import { SeoService } from 'src/app/seo.service';

@Component({
  selector: 'app-blog-details-page',
  templateUrl: './blog-details-page.component.html',
  styleUrls: ['./blog-details-page.component.scss'],
  providers: [{ provide: LOCALE_ID, useValue: 'fr' }]
})
export class BlogDetailsPageComponent implements OnInit {
  public WEBSITE_URL = environment.WEBSITE_URL;
  public data: any;
  public current_post: any;
  public related_posts: any;
  articleUrl = '';
  copied = false;
  private startTime = Date.now();

  // 🔥 NOUVEAU : État de chargement
  isLoading = true;
  hasError = false;
  errorMessage = '';

  shareLinks = {
    facebook: '',
    twitter: '',
    linkedin: ''
  };

  blogSlides: OwlOptions = {
    nav: true,
    loop: true,
    margin: 25,
    dots: false,
    autoplay: false,
    smartSpeed: 500,
    autoplayHoverPause: true,
    navText: [
      "<i class='ri-arrow-left-s-line'></i>",
      "<i class='ri-arrow-right-s-line'></i>"
    ],
    responsive: {
        0: {
          items: 1
        },
        576: {
          items: 1
        },
        768: {
          items: 2
        },
        992: {
          items: 2
        },
        1200: {
          items: 3
        }
    }
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private route: ActivatedRoute,
    private router: Router,
    private blogService: BlogDetailsPageService,
    private relatedPostsDataService: RelatedPostsDataService,
    private recommendedService: RecommendedArticlesService,
    private sanitizer: DomSanitizer,
    public dialog: MatDialog,
    private seo: SeoService,
    private ga: GoogleAnalyticsService
  ) {}

  ngOnInit(): void {
    
    if (isPlatformBrowser(this.platformId)) {

      this.articleUrl = window.location.href;
      const currentUrl = encodeURIComponent(this.articleUrl);
  
      this.shareLinks.facebook = `https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`;
      this.shareLinks.twitter = `https://twitter.com/intent/tweet?url=${currentUrl}`;
      this.shareLinks.linkedin = `https://www.linkedin.com/shareArticle?mini=true&url=${currentUrl}`;
    }

    this.route.params.subscribe(param => {
      // 🔥 NOUVEAU : Reset des états à chaque changement de route
      this.isLoading = true;
      this.hasError = false;
      this.current_post = null;
      
      this.blogService.getPostBySlug(param['slug']).subscribe({
        next: (res: any) => {
          this.data = res?.data || [];
          this.current_post = this.data[0];

          if (this.current_post) {
            const title = this.current_post?.attributes?.title || 'AfroTech Hub';
            const description = this.current_post?.attributes?.description || 'Découvre les avancées technologiques en Afrique';
            const image = this.current_post?.attributes?.image?.data?.attributes?.url;
            
            const url = `https://afrotech-hub.com/news/${this.current_post?.attributes?.slug}`;

            const keywords = this.current_post?.attributes?.excerpt;
            
            this.seo.updateMeta(title, description, image, url, keywords);

            // 🔥 TRACKING GA : Page vue d'article
            this.ga.trackPageView(url);
            
            // 🔥 TRACKING GA : Événement de lecture d'article
            this.ga.trackEvent('article_view', {
              category: 'blog',
              label: title,
              article_id: this.current_post.id,
              article_title: title,
              article_category: this.getTags().map((tag: any) => tag.attributes?.name).join(', ')
            });

            const tags_id = this.current_post?.attributes?.post_tags?.data?.map((tag: any) => tag.id) || [];

            if (tags_id.length > 0) {
              this.blogService.getRelatedPosts(tags_id, this.current_post.id).subscribe((related: any) => {
                this.related_posts = related;
                // 🔥 NOUVEAU : Arrêter le loading après avoir récupéré les posts reliés
                this.isLoading = false;
              });
            } else {
              // 🔥 NOUVEAU : Arrêter le loading même sans posts reliés
              this.isLoading = false;
            }

            this.recommendedService.getRecommendedArticles(this.current_post.id);
          } else {
            // 🔥 NOUVEAU : Gérer le cas où l'article n'existe pas
            this.hasError = true;
            this.errorMessage = 'Article introuvable';
            this.isLoading = false;
          }
        },
        error: (error) => {
          // 🔥 NOUVEAU : Gérer les erreurs de chargement
          console.error('Erreur lors du chargement de l\'article:', error);
          this.hasError = true;
          this.errorMessage = 'Erreur lors du chargement de l\'article';
          this.isLoading = false;
        }
      });
    });
  }

  ngOnDestroy() {
    if (this.current_post && isPlatformBrowser(this.platformId)) {
      const timeSpent = Math.round((Date.now() - this.startTime) / 1000);
      
      this.ga.trackEvent('article_engagement', {
        category: 'engagement',
        label: 'time_spent',
        value: timeSpent,
        article_title: this.current_post?.attributes?.title
      });
    }
  }

  openDialog(articleSlug: string, articleTitle: string) {

    // 🔥 TRACKING GA : Ouverture du modal de partage
    this.ga.trackEvent('share_modal_open', {
      category: 'engagement',
      label: articleTitle,
      article_slug: articleSlug
    });
    
    this.dialog.open(ShareSocialMediaModalComponent, {
      data: { articleSlug, articleTitle, page: 'blog' }
    });
  }

  getTags() {
    return this.current_post?.attributes?.post_tags?.data || [];
  }

  normalizeTag(tag: string): string {
    return tag.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-');
  }

  copyArticleLink() {
    if (isPlatformBrowser(this.platformId) && this.articleUrl) {
      navigator.clipboard.writeText(this.articleUrl).then(() => {
        this.copied = true;
        // 🔥 TRACKING GA : Copie du lien
        this.ga.trackEvent('article_link_copy', {
          category: 'engagement',
          label: this.current_post?.attributes?.title,
          article_url: this.articleUrl
        });
        if (isPlatformBrowser(this.platformId)) {
          setTimeout(() => (this.copied = false), 2000);
        }
      });
    }
  }

  sendRelatedPostsData() {
    this.relatedPostsDataService.data = this.related_posts;
  }

  async convertMarkdownToHtml(markdown: string): Promise<SafeHtml> {
    const html = await marked.parse(markdown || '');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}